import { useMemo, useEffect } from 'react';
import { TestTube, User } from 'lucide-react';
import { AutocompleteSelect } from '@/Components/shared/AutocompleteSelect';
import { UserSelector } from '@/features/contracts/components/form-fields/UserSelector';
import { FormInput } from '@/Components/shared/FormInput';
import { PhoneInput } from '@/Components/shared/PhoneInput';
import { AddressAutocomplete } from '@/Components/shared/AddressAutocomplete';
import { useAuth } from '@/hooks/auth/useAuth';
import { usePermissions } from '@/hooks/auth/usePermissions';
import { useAvailableSocieties } from '@/routes/outil-pro/hooks/societies/useAvailableSocieties';
import { EligibleSocietySelector } from '@/features/societies/components/EligibleSocietySelector';
import { useEffectiveConfiguration } from '@/features/societies/hooks/useSocietyConfiguration';
import { Checkbox } from '@/Components/ui/Checkbox';

import type { ContractFormState } from '@/features/contracts/hooks/useContractFormWizard';
import type { Society } from '@/features/societies/types/society.types';
import type { LocationResult } from '@/services/geocoding.service';

interface Step1Props {
  formData: ContractFormState;
  updateFormData: (updates: Partial<ContractFormState>) => void;
  onCanProceedChange: (canProceed: boolean) => void;
  isSocietyLocked?: boolean;
  showErrors?: boolean;
}

const TEST_BENEFICIARY_DATA = {
  firstName: 'Jean',
  lastName: 'Dupont',
  email: 'testprov2@affinicar.com',
  phoneNumber: '+33651108592',
  address: '123 Avenue des Champs-Élysées',
  address2: 'Bâtiment A, 3ème étage',
  cp: '75008',
  city: 'Paris',
};

/**
 * Les garanties proposées dépendent de la stratégie de la société émettrice :
 * changer de société invalide la garantie déjà choisie (sinon un contrat agence
 * pourrait partir avec une garantie de la tête de groupe).
 */
const WARRANTY_RESET: Partial<ContractFormState> = {
  warrantyUuid: undefined,
  warrantyName: undefined,
  customWarranty: undefined,
  customWarrantyRaw: undefined,
  customWarrantyPartIds: undefined,
};

/**
 * Step 1 : Bénéficiaire (Société + Utilisateur + Identité/Contact/Adresse)
 */
export function Step1Beneficiary({ formData, updateFormData, onCanProceedChange, isSocietyLocked = false, showErrors = false }: Step1Props) {
  const { user } = useAuth();
  const { can } = usePermissions();
  const isInternalUser = user?.type === 'INTERNAL';
  const isExternalUser = user?.type === 'EXTERNAL';
  const { societies: eligibleSocieties } = useAvailableSocieties('contract');
  const isExternalMultiSociety = isExternalUser && eligibleSocieties.length > 1;
  const showSocietySelector = (isInternalUser || isExternalMultiSociety) && !isSocietyLocked;

  // Config effective de la société émettrice (merge groupe + propre).
  // On en dérive « upsell activé » via `upsellMandateSigned`. Ce flag peut être
  // absent (undefined) tant que le backend ne l'expose pas sur la config
  // effective : dans ce cas la case RGPD reste masquée (comportement = upsell off).
  const { data: effectiveConfig } = useEffectiveConfiguration(formData.societyId);
  const isUpsellEnabled = effectiveConfig?.upsellMandateSigned === true;

  const handleExternalSocietyChange = (uuid: string | undefined) => {
    updateFormData({
      ...(uuid !== formData.societyId ? WARRANTY_RESET : {}),
      societyId: uuid,
      userId: user?.id,
      userName: user?.firstname && user?.lastname
        ? { firstname: user.firstname, lastname: user.lastname }
        : undefined,
    });
  };

  // Auto-sélection de la société pour un external user mono-société éligible
  useEffect(() => {
    if (
      isExternalUser &&
      !isSocietyLocked &&
      !formData.societyId &&
      eligibleSocieties.length === 1
    ) {
      handleExternalSocietyChange(eligibleSocieties[0].societyId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExternalUser, isSocietyLocked, formData.societyId, eligibleSocieties]);

  const canUseTestData = useMemo(() => {
    return isInternalUser && can('contract.use_test_data');
  }, [isInternalUser, can]);

  const canProceed = useMemo(() => {
    return !!formData.societyId &&
           !!formData.userId &&
           !!formData.beneficiary.firstName &&
           !!formData.beneficiary.lastName &&
           !!formData.beneficiary.email &&
           !!formData.beneficiary.phoneNumber;
  }, [formData]);

  useEffect(() => {
    onCanProceedChange(canProceed);
  }, [canProceed, onCanProceedChange]);

  const handleBeneficiaryChange = (field: keyof typeof formData.beneficiary, value: string) => {
    const updatedBeneficiary = { ...formData.beneficiary, [field]: value };
    updateFormData({
      beneficiary: updatedBeneficiary,
      subscriber: updatedBeneficiary,
    });
  };

  const handleAddressSelect = (location: LocationResult) => {
    const updatedBeneficiary = {
      ...formData.beneficiary,
      address: location.streetAddress || location.address,
      cp: location.postalCode || '',
      city: location.city || '',
    };
    updateFormData({
      beneficiary: updatedBeneficiary,
      subscriber: updatedBeneficiary,
    });
  };

  const handleFillTestData = () => {
    const updatedBeneficiary = { ...formData.beneficiary, ...TEST_BENEFICIARY_DATA };
    updateFormData({
      beneficiary: updatedBeneficiary,
      subscriber: updatedBeneficiary,
    });
  };

  return (
    <div className="tw-max-w-6xl tw-mx-auto tw-p-4">
      {/* Bloc Société + Utilisateur - INTERNAL uniquement */}
      {isInternalUser && (
        <div id="society-user-block" className="tw-p-6">
          <div className="tw-bg-affinicar-primary/5 tw-rounded-xl tw-p-6 tw-border-l-4 tw-border-l-grey tw-space-y-4">
            <p className="tw-text-sm tw-text-gray-500 tw-mb-0">
              Sélectionnez la société et l'utilisateur pour lesquels créer le contrat
            </p>
            <div className="tw-space-y-4">
              {/* Société - Affichée seulement si non verrouillée */}
              {showSocietySelector && (
                <div>
                  <AutocompleteSelect<Society>
                    label="Société"
                    required
                    apiEndpoint="/societies"
                    searchParam="nameFilter"
                    getOptionLabel={(society) => society.name}
                    getOptionValue={(society) => society.uuid}
                    value={formData.societyId || null}
                    onChange={(uuid) => {
                      updateFormData({
                        ...((uuid || undefined) !== formData.societyId ? WARRANTY_RESET : {}),
                        societyId: uuid || undefined,
                        userId: undefined,
                        userName: undefined,
                      });
                    }}
                    placeholder="Rechercher une société..."
                    renderOption={(society) => (
                      <div>
                        <div className="tw-font-medium">{society.name}</div>
                        {society.rootParentName && (
                          <div className="tw-text-xs tw-text-gray-500">
                            Groupe : {society.rootParentName}
                          </div>
                        )}
                      </div>
                    )}
                  />
                </div>
              )}

              {/* Utilisateur - Affiché uniquement si société sélectionnée */}
              {formData.societyId && (
                <div>
                  <UserSelector
                    societyId={formData.societyId}
                    value={formData.userId}
                    onChange={(userId, userName) => updateFormData({ userId, userName })}
                    required
                    error={showErrors && !formData.userId ? 'L\'utilisateur est requis' : undefined}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Bloc Société - EXTERNAL multi-société : restreint aux sociétés autorisées à créer des contrats */}
      {isExternalMultiSociety && !isSocietyLocked && (
        <div id="society-external-block" className="tw-p-6">
          <div className="tw-bg-affinicar-primary/5 tw-rounded-xl tw-p-6 tw-border-l-4 tw-border-l-grey tw-space-y-4">
            <p className="tw-text-sm tw-text-gray-500 tw-mb-0">
              Sélectionnez la société émettrice du contrat
            </p>
            <EligibleSocietySelector
              action="contract"
              value={formData.societyId}
              onChange={handleExternalSocietyChange}
              required
              dataTestId="contract-external-society-selector"
            />
          </div>
        </div>
      )}

      {/* Bloc Bénéficiaire */}
      {(isInternalUser || isExternalMultiSociety) && <div className="tw-h-px tw-bg-affinicar-primary tw-rounded-full tw-mx-6 tw-my-6" />}
      <div id="beneficiary-fields-block" className="tw-relative tw-p-6 tw-space-y-5">

        {/* Bouton données de test */}
        {canUseTestData && (
          <button
            type="button"
            onClick={handleFillTestData}
            className="tw-absolute tw-top-4 tw-right-4 tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-gray-600 tw-bg-gray-100 hover:tw-bg-gray-200 tw-border tw-border-gray-300 tw-rounded-lg tw-flex tw-items-center tw-gap-2 tw-transition-colors"
            title="Remplir avec des données de test"
          >
            <TestTube className="tw-w-4 tw-h-4" />
            Données test
          </button>
        )}

        {/* Titre principal */}
        <div className="tw-flex tw-items-center tw-gap-2 tw-mb-1">
          <User className="tw-w-4 tw-h-4 tw-text-affinicar-primary" />
          <h4 className="tw-text-sm tw-font-semibold tw-text-gray-900 tw-mb-0">Informations bénéficiaire</h4>
        </div>

        {/* Section Identité */}
        <div id="beneficiary-identity-block" className="tw-bg-affinicar-primary/5 tw-rounded-xl tw-p-6 tw-border-l-4 tw-border-l-grey tw-space-y-4">
          <p className="tw-text-sm tw-text-gray-500 tw-mb-0">
            Renseignez l'identité du bénéficiaire telle qu'elle apparaîtra sur le contrat de garantie
          </p>
          <FormInput
            label="Nom"
            value={formData.beneficiary.lastName || ''}
            onChange={(val) => handleBeneficiaryChange('lastName', val)}
            required
            placeholder="Dupont"
            valid={!!formData.beneficiary.lastName}
            error={showErrors && !formData.beneficiary.lastName ? 'Le nom est requis' : undefined}
            tooltip="Nom de famille du bénéficiaire de la garantie tel qu'il apparaîtra sur le contrat"
          />
          <FormInput
            label="Prénom"
            value={formData.beneficiary.firstName || ''}
            onChange={(val) => handleBeneficiaryChange('firstName', val)}
            required
            placeholder="Jean"
            valid={!!formData.beneficiary.firstName}
            error={showErrors && !formData.beneficiary.firstName ? 'Le prénom est requis' : undefined}
            tooltip="Prénom du bénéficiaire de la garantie tel qu'il apparaîtra sur le contrat"
          />
          <FormInput
            label="Société du bénéficiaire"
            value={formData.beneficiary.society || ''}
            onChange={(val) => handleBeneficiaryChange('society', val)}
            placeholder="Nom de la société (si applicable)"
            tooltip="Renseignez si le bénéficiaire est une entreprise (achat professionnel)"
          />
        </div>

        {/* Section Coordonnées & Adresse */}
        <div id="beneficiary-contact-block" className="tw-bg-affinicar-primary/5 tw-rounded-xl tw-p-6 tw-border-l-4 tw-border-l-grey tw-space-y-4">
          <p className="tw-text-sm tw-text-gray-500 tw-mb-0">
            Coordonnées de contact et adresse postale du bénéficiaire pour l'envoi du contrat et le suivi SAV
          </p>
          <FormInput
            type="email"
            label="Email"
            value={formData.beneficiary.email || ''}
            onChange={(val) => handleBeneficiaryChange('email', val)}
            required
            placeholder="exemple@domain.com"
            valid={!!formData.beneficiary.email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.beneficiary.email)}
            error={showErrors && !formData.beneficiary.email ? 'L\'email est requis' : undefined}
            tooltip="Adresse email du bénéficiaire."
          />
          <PhoneInput
            value={formData.beneficiary.phoneNumber || ''}
            onChange={(val) => handleBeneficiaryChange('phoneNumber', val)}
            required
            error={showErrors && !formData.beneficiary.phoneNumber ? 'Le téléphone est requis' : undefined}
            tooltip="Numéro de téléphone du bénéficiaire."
          />
          <AddressAutocomplete
            value={formData.beneficiary.address || ''}
            onLocationSelect={handleAddressSelect}
            onChange={(value) => handleBeneficiaryChange('address', value)}
            placeholder="Rechercher l'adresse..."
            searchType="address"
            label="Adresse"
            required
            tooltip="Adresse postale du bénéficiaire, utilisée pour le contrat. La recherche propose des suggestions automatiques"
          />
          <FormInput
            label="Complément d'adresse"
            value={formData.beneficiary.address2 || ''}
            onChange={(val) => handleBeneficiaryChange('address2', val)}
            placeholder="Bâtiment, étage..."
            tooltip="Bâtiment, étage, code d'accès ou toute précision utile"
          />
          <FormInput
            label="Code postal"
            value={formData.beneficiary.cp || ''}
            onChange={(val) => handleBeneficiaryChange('cp', val)}
            maxLength={5}
            placeholder="75001"
            required
            tooltip="Code postal du bénéficiaire"
          />
          <FormInput
            label="Ville"
            value={formData.beneficiary.city || ''}
            onChange={(val) => handleBeneficiaryChange('city', val)}
            placeholder="Paris"
            required
            tooltip="Ville de résidence du bénéficiaire"
          />
        </div>

        {/* RGPD — refus de prospection.
            Affiché uniquement si le vendeur a l'upsell activé (config effective
            `upsellMandateSigned`). Défaut décoché ; transmis en `prospectionOptOut`. */}
        {isUpsellEnabled && (
          <div id="beneficiary-prospection-block" className="tw-bg-affinicar-primary/5 tw-rounded-xl tw-p-6 tw-border-l-4 tw-border-l-grey">
            <Checkbox
              checked={formData.prospectionOptOut}
              onChange={(checked) => updateFormData({ prospectionOptOut: checked })}
              label="Le client refuse que ses données soient utilisées à des fins de prospection"
            />
          </div>
        )}
      </div>
    </div>
  );
}
